import { Select } from '@advanza/advanza_generic'
import { PreIcon } from '@advanza/ui'
import { useAppSelector } from 'hooks/hooks'
import Service from 'interfaces/Services'
import { useEffect, useState } from 'react'

interface Props {
    entity?: Partial<Service>
    onChangeEntity?: (diff: Partial<Service>, additional?: Record<string, unknown>) => void
}

/**
 * Calendar Picker
 *
 * A service is not allowed to have both a day and a month picker
 * This component enforces that only of those options can be selected.
 */
const CalendarPicker = ({ entity: service = {}, onChangeEntity = () => {} }: Props) => {
    const { isFetching } = useAppSelector((state) => state.services)
    const [currentCalendar, setCurrentCalendar] = useState('')
    const [showWarning, setShowWarning] = useState(false)
    const loadData = service.service_id && !isFetching && !service._saving

    const getSelected = () =>
        service.enable_calendar
            ? 'enable_calendar'
            : service.enable_month_picker
              ? 'enable_month_picker'
              : 'no_calendar'

    const onChangeCalendar = (e: { target: { value: string } }) => {
        const { value: newCalendar } = e.target

        // changing an existing calendar type likely cause issues in the wizard and supply dashboard
        if (currentCalendar !== newCalendar && currentCalendar !== 'no_calendar') {
            setShowWarning(true)
        } else {
            setShowWarning(false)
        }

        // reset all calendars
        const calendars: Pick<Service, 'enable_calendar' | 'enable_month_picker'> = {
            enable_calendar: false,
            enable_month_picker: false,
        }

        if (newCalendar !== 'no_calendar') {
            // set the one we want to true
            calendars[newCalendar as keyof typeof calendars] = true
        }

        onChangeEntity(calendars)
    }

    useEffect(() => {
        if (loadData) {
            // after fetching or saving the service, we need to update the current calendar
            setCurrentCalendar(getSelected())

            // remove the warning if needed
            showWarning && setShowWarning(false)
        }
    }, [loadData])

    return (
        <div>
            <Select
                msg="Enable day or month calendar"
                value={getSelected()}
                options={[
                    { value: 'no_calendar', title: 'None' },
                    { value: 'enable_calendar', title: 'Day picker' },
                    { value: 'enable_month_picker', title: 'Month picker' },
                ]}
                onChange={onChangeCalendar}
            />
            {showWarning && (
                <PreIcon icon="warning" red overflowFix>
                    <strong style={{ color: '#cf3131', padding: '4px' }}>
                        Are you sure you want to change the type of calendar? Changing it will
                        likely cause issues in the wizard and supply dashboard.
                    </strong>
                </PreIcon>
            )}
        </div>
    )
}

export default CalendarPicker
